import { Extension, isNodeEmpty, isNodeSelection } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

export interface EmptyNodeOptions {
  /**
   * **The class name for the empty editor**
   * @default 'is-editor-empty'
   */
  emptyEditorClass: string;

  /**
   * **The class name for empty nodes**
   * @default 'is-empty'
   */
  emptyNodeClass: string;
}

/**
 * Extension that adds classes if the currently focused node is empty via decorations.
 */
export const EmptyNode = Extension.create<EmptyNodeOptions>({
  name: "emptyNodeClass",

  addOptions() {
    return {
      emptyEditorClass: "is-editor-empty",
      emptyNodeClass: "is-empty",
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("emptyNode"),
        props: {
          decorations: ({ doc, selection }) => {
            const active = this.editor.isEditable;
            const { anchor } = selection;
            const decorations: Decoration[] = [];

            if (!active) {
              return null;
            }

            const isEmptyDoc = this.editor.isEmpty;

            doc.descendants((node, pos) => {
              const hasAnchor =
                !isNodeSelection(selection) && anchor >= pos && anchor <= pos + node.nodeSize;

              const isEmpty = !node.isLeaf && isNodeEmpty(node);

              if (hasAnchor && isEmpty) {
                const classes = [this.options.emptyNodeClass];

                if (isEmptyDoc) {
                  classes.push(this.options.emptyEditorClass);
                }

                const decoration = Decoration.node(pos, pos + node.nodeSize, {
                  class: classes.join(" "),
                });

                decorations.push(decoration);
              }

              return false;
            });

            return DecorationSet.create(doc, decorations);
          },
        },
      }),
    ];
  },
});
